import { API_ENDPOINTS } from '../endpoints';
import { getAuthHeaders, handleResponse } from '../client';
import type { ErpRecordVO, MatchNoteVO, MatrixSummaryVO, MonitorOverviewVO, NodeAnalysisProjectsPagedVO, NodeAnalysisSummaryVO, NodeDetailVO, PageResponse, ProjectMonitorDetailVO, RiskRankingItemVO } from '../types';

export const monitorViewApi = {
  getOverview: async (params?: {
    brand?: string;
    keyword?: string;
  }): Promise<MonitorOverviewVO> => {
    const queryParams = new URLSearchParams();
    if (params?.brand) queryParams.append('brand', params.brand);
    if (params?.keyword) queryParams.append('keyword', params.keyword);

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.OVERVIEW}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<MonitorOverviewVO>(response);
  },

  getRiskRanking: async (params?: {
    brand?: string;
    projectCode?: string;
    projectName?: string;
    pageNum?: number;
    pageSize?: number;
  }): Promise<PageResponse<RiskRankingItemVO>> => {
    const queryParams = new URLSearchParams();
    if (params?.brand) queryParams.append('brand', params.brand);
    if (params?.projectName) queryParams.append('projectName', params.projectName);
    if (params?.projectCode) queryParams.append('projectCode', params.projectCode);
    queryParams.append('pageNum', String(params?.pageNum ?? 1));
    queryParams.append('pageSize', String(params?.pageSize ?? 10));

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.RISK_RANKING}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PageResponse<RiskRankingItemVO>>(response);
  },

  getMatrix: async (params?: {
    brand?: string;
    keyword?: string;
    keyNodeIds?: number[];
    nodeStatuses?: string[];
    riskFlags?: string[];
    pageNum?: number;
    pageSize?: number;
  }): Promise<MatrixSummaryVO> => {
    const queryParams = new URLSearchParams();
    if (params?.brand) queryParams.append('brand', params.brand);
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    if (params?.keyNodeIds?.length) queryParams.append('keyNodeIds', params.keyNodeIds.join(','));
    if (params?.nodeStatuses?.length) queryParams.append('nodeStatuses', params.nodeStatuses.join(','));
    if (params?.riskFlags?.length) queryParams.append('riskFlags', params.riskFlags.join(','));
    queryParams.append('pageNum', String(params?.pageNum ?? 1));
    queryParams.append('pageSize', String(params?.pageSize ?? 10));

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.MATRIX}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<MatrixSummaryVO>(response);
  },

  getNodeAnalysis: async (params?: {
    brand?: string;
  }): Promise<NodeAnalysisSummaryVO[]> => {
    const queryParams = new URLSearchParams();
    if (params?.brand) queryParams.append('brand', params.brand);

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.NODE_ANALYSIS}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<NodeAnalysisSummaryVO[]>(response);
  },

  getNodeAnalysisProjects: async (params: {
    keyNodeId: number;
    brand?: string;
    keyword?: string;
    dataSource?: string;
    riskFlag?: string;
    pageNum?: number;
    pageSize?: number;
  }): Promise<NodeAnalysisProjectsPagedVO> => {
    const queryParams = new URLSearchParams();
    queryParams.append('keyNodeId', String(params.keyNodeId));
    if (params.brand) queryParams.append('brand', params.brand);
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.dataSource) queryParams.append('dataSource', params.dataSource);
    if (params.riskFlag) queryParams.append('riskFlag', params.riskFlag);
    queryParams.append('pageNum', String(params.pageNum ?? 1));
    queryParams.append('pageSize', String(params.pageSize ?? 10));

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.NODE_ANALYSIS_PROJECTS}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<NodeAnalysisProjectsPagedVO>(response);
  },

  getProjectDetail: async (code: string): Promise<ProjectMonitorDetailVO> => {
    const response = await fetch(API_ENDPOINTS.MONITOR_VIEW.PROJECT_DETAIL(code), {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProjectMonitorDetailVO>(response);
  },

  getNodeDetail: async (params: { nodeId: number }): Promise<NodeDetailVO> => {
    const queryParams = new URLSearchParams();
    queryParams.append('nodeId', String(params.nodeId));

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.NODE_DETAIL}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<NodeDetailVO>(response);
  },

  getErpRecords: async (params: {
    projectCode: string;
    pageNum?: number;
    pageSize?: number;
  }): Promise<PageResponse<ErpRecordVO>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('projectCode', params.projectCode);
    queryParams.append('pageNum', String(params?.pageNum ?? 1));
    queryParams.append('pageSize', String(params?.pageSize ?? 10));

    const response = await fetch(`${API_ENDPOINTS.MONITOR_VIEW.ERP_RECORDS}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PageResponse<ErpRecordVO>>(response);
  },
};

function normalizeMatchNote(note: MatchNoteVO): MatchNoteVO {
  return {
    ...note,
    createdAt: note.createdAt ?? note.createTime,
  };
}
